import { useState, useEffect, useContext, ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { ProtectContext } from "../../context/protect_context";
import { IMAGE_BASE_URL } from "../../utils/image_url";
import { ScrollProgress } from "./ScrollProgress";
import { Footer } from "./Footer";
import { MobileNav } from "./MobileNav";
import type { Page } from "../../types";

type LayoutProps = {
  children: ReactNode;
};

export const Layout = ({ children }: LayoutProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);
  const { data }: any = useContext(ProtectContext);
  const location = useLocation();
  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname === `/${href}`;
  };
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);
  return (
    <div className="flex min-h-screen flex-col bg-white text-gray-900">
      <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur">
        <nav className="mx-auto w-full max-w-7xl px-4 py-4 lg:px-8">
          <div className="flex w-full flex-wrap items-center gap-y-4 lg:flex-nowrap lg:gap-8">
            <div className="flex w-full items-center justify-between lg:w-auto">
              <Link
                to="/"
                className="flex items-center gap-3"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                {data?.config?.logo ? (
                  <img
                    src={`${IMAGE_BASE_URL}${data.config.logo}`}
                    alt="Бэлэн бай"
                    className="h-7 lg:h-10 w-auto"
                  />
                ) : null}
              </Link>
              <button
                type="button"
                className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 text-gray-600 transition hover:border-red-300 hover:text-red-500 lg:hidden"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Цэс"
                aria-expanded={mobileMenuOpen}
              >
                <span className="sr-only">Цэс нээх</span>
                <svg
                  className="h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4.5 6.75h15m-15 5.25h15m-15 5.25h15"
                  />
                </svg>
              </button>
            </div>

            <div className="hidden flex-1 justify-center lg:flex">
              <ul className="flex items-center gap-4 text-sm font-semibold text-gray-700">
                {(data?.datas ?? []).slice(1, (data?.datas ?? []).length)
                  .map((item: Page) => (
                    <li
                      key={item.page}
                      className={`group relative ${
                        item.icon === "location" ? "lg:ml-[70px]" : ""
                      }`}
                    >
                      <Link
                        to={item.href}
                        className={`flex items-center gap-2 whitespace-nowrap rounded-full px-3 py-2 transition text-sm font-bold ${
                          isActive(item.href)
                            ? "bg-red-50 text-red-500"
                            : "hover:text-red-500"
                        }`}
                        onClick={handleLinkClick}
                      >
                        {item.page}
                      </Link>
                      {(item.pages?.length ?? 0) > 0 && (
                        <div className="invisible absolute left-1/2 top-full mt-3 w-56 -translate-x-1/2 rounded-2xl border border-gray-100 bg-white p-3 opacity-0 shadow-xl transition-all duration-200 group-hover:visible group-hover:translate-y-2 group-hover:opacity-100">
                          <ul className="space-y-1 text-sm font-medium text-gray-600">
                            {(item.pages ?? []).map((subPage: Page) => (
                              <li key={subPage.page}>
                                <Link
                                  to={subPage.href}
                                  className={`block rounded-lg px-3 py-2 transition ${
                                    isActive(subPage.href)
                                      ? "bg-red-50 text-red-500"
                                      : "hover:bg-red-50 hover:text-red-500"
                                  }`}
                                  onClick={handleLinkClick}
                                >
                                  {subPage.page}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
            <div className="hidden lg:flex gap-2">
              <Link
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                to={"/forum"}
              >
                <button
                  type="button"
                  className="rounded-full px-6 py-2.5 text-sm font-bold text-white"
                  style={{
                    background: "linear-gradient(to right, #f97316, #ef4444)",
                  }}
                >
                  Форум
                </button>
              </Link>
              {/* <button
                type="button"
                className="rounded-full px-6 py-2.5 text-sm font-bold text-white"
                style={{
                  background: "linear-gradient(to right, #f97316, #ef4444)",
                }}
              >
                Нэвтрэх
              </button> */}
            </div>
          </div>
        </nav>
        <MobileNav
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        />
      </header>

      <main className="flex-1">{children}</main>

      <Footer />

      <ScrollProgress />
    </div>
  );
};
